'use client';

import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import useClickOutside from '@/hooks/useClickOutside';
import { ShadowBox } from '@/ui-components/atoms/ShadowBox/ShadowBox';
import styles from './snapshotpopover.module.scss';

const MOBILE_BREAKPOINT_PX = 768;
const POPOVER_VERTICAL_OFFSET_PX = 8;

interface SnapshotPopoverShellProps {
    ariaLabel: string;
    ariaLabelledBy?: string;
    buttonRef: RefObject<HTMLButtonElement | null>;
    children: React.ReactNode;
    id?: string;
    onClose: () => void;
    open: boolean;
    className?: string;
}

export function SnapshotPopoverShell({
    ariaLabel,
    ariaLabelledBy,
    buttonRef,
    children,
    id,
    onClose,
    open,
    className,
}: SnapshotPopoverShellProps): React.JSX.Element | null {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [mobileTop, setMobileTop] = useState<number | null>(null);

    useClickOutside(
        wrapperRef,
        {},
        () => {
            if (open) {
                onClose();
            }
        },
        buttonRef,
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        const updatePosition = () => {
            const triggerButton = buttonRef.current;

            if (!triggerButton || window.innerWidth >= MOBILE_BREAKPOINT_PX) {
                setMobileTop(null);
                return;
            }

            const { bottom } = triggerButton.getBoundingClientRect();
            setMobileTop(bottom + POPOVER_VERTICAL_OFFSET_PX);
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [buttonRef, open]);

    if (!open) {
        return null;
    }

    const resolvedMobileTop = mobileTop;
    const popoverStyle: CSSProperties | undefined =
        resolvedMobileTop === null ? undefined : { top: `${resolvedMobileTop}px` };

    return (
        <div
            id={id}
            ref={wrapperRef}
            className={cn(styles.snapshotPopover, className, {
                [styles.snapshotPopoverMobile]: resolvedMobileTop !== null,
            })}
            style={popoverStyle}
            role="dialog"
            aria-modal="false"
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
        >
            <ShadowBox targetClass={styles.snapshotPopoverCard}>{children}</ShadowBox>
        </div>
    );
}
